/*eslint max-lines-per-function: ["error", 230]*/

import React, { useEffect, useState } from 'react';
import Card from 'react-bootstrap/Card';
import { Form, InputGroup } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import { FormSelect } from '../../../app/components/FormSelect';
import {
  CreateUpdateExaminedDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { CreatableClearableSelector } from '../Selects/CreatableClearableSelector';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { CompanyGusButton } from '../Company/CompanyGusButton';

interface ExaminedEditorRightSideProps {
  propertyChange: (obj: Partial<CreateUpdateExaminedDto>) => void;
  readOnly: boolean;
  show?: boolean;
  examined: CreateUpdateExaminedDto;
}

export const ExaminedEditorRightSide: React.FC<
  ExaminedEditorRightSideProps
> = props => {
  const [examined, setExamined] = useState(props.examined);
  const [otherPostalAddress, setOtherPostalAddress] = useState(false);
  const [isGusLoading, setIsGusLoading] = useState(false);
  const [gusErrors, setGusErrors] = useState<string[]>([]);
  const spdCore = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setOtherPostalAddress(props.examined?.postalAddress !== undefined);
      setExamined(props.examined);
    }
  }, [props.show, props.examined]);

  function onPostalCheckChanged(): void {
    setOtherPostalAddress(!otherPostalAddress);
  }

  const propertyChange = (obj: Partial<CreateUpdateExaminedDto>) => {
    setExamined({ ...examined, ...obj });
    props.propertyChange(obj);
  };

  const textOrEmpty = (text: string | null | undefined) => text || '';

  const findByNip = async () => {
    const nip = examined?.nip?.trim();
    if (!nip) {
      return;
    }
    setIsGusLoading(true);
    setGusErrors([]);
    try {
      const nipClient = await spdCore.companies.getByNip(nip);
      if (nipClient) {
        const address = [
          nipClient.street,
          nipClient.numbers,
          nipClient.flat ? `/${nipClient.flat}` : '',
          nipClient.postcode,
          nipClient.place,
        ]
          .filter(Boolean)
          .join(' ')
          .trim();
        propertyChange({
          invoiceName: nipClient.name || examined.invoiceName,
          invoiceAddress: address || examined.invoiceAddress,
        });
      }
    } catch (response) {
      setGusErrors(await responseErrors(response as ResponseError));
    } finally {
      setIsGusLoading(false);
    }
  };

  return (
    <>
      <Card>
        <Card.Header>Dodatkowe dane</Card.Header>
        <Card.Body>
          <Form className="d-grid gap-3">
            <FormRow controlId="positive" label="Wykształcenie">
              <FormSelect
                value={examined?.education}
                options={[
                  { value: 'Wyższe', label: 'Wyższe' },
                  { value: 'Średnie', label: 'Średnie' },
                  { value: 'Zasadnicze', label: 'Zasadnicze' },
                  { value: 'Podstawowe', label: 'Podstawowe' },
                ]}
                onChange={value => propertyChange({ education: value })}
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="learned_profession" label="Zawód wyuczony">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.learnedProfession)}
                onChange={e =>
                  propertyChange({ learnedProfession: e.target.value })
                }
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="postcode" label="Kod pocztowy">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.postcode)}
                onChange={e => propertyChange({ postcode: e.target.value })}
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="city" label="Miejscowość">
              <CreatableClearableSelector
                value={textOrEmpty(examined?.city)}
                onChange={chosen => propertyChange({ city: chosen.label })}
                provider={spdCore.examineds.getCitiesByName}
                readOnly={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="street" label="Ulica">
              <CreatableClearableSelector
                value={textOrEmpty(examined?.street)}
                onChange={chosen => propertyChange({ street: chosen.label })}
                provider={spdCore.examineds.getStreetsByName}
                readOnly={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="numbers" label=" Numer">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.numbers)}
                onChange={e => propertyChange({ numbers: e.target.value })}
                disabled={props.readOnly}
              />
            </FormRow>
            {!props.readOnly && (
              <FormRow controlId="postalAddressChck" label="">
                <Form.Check
                  checked={otherPostalAddress}
                  label="Inny adres korespondencyjny"
                  onChange={() => onPostalCheckChanged()}
                />
              </FormRow>
            )}
            {otherPostalAddress && (
              <FormRow controlId="postalAddress" label="Adres korespond.">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={textOrEmpty(examined?.postalAddress)}
                  onChange={e =>
                    propertyChange({ postalAddress: e.target.value })
                  }
                  disabled={props.readOnly || !otherPostalAddress}
                />
              </FormRow>
            )}

            <FormRow controlId="email" label="E-mail">
              <Form.Control
                type="email"
                value={textOrEmpty(examined?.email)}
                onChange={e => propertyChange({ email: e.target.value })}
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="phone" label="Telefon">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.phone)}
                onChange={e => propertyChange({ phone: e.target.value })}
                disabled={props.readOnly}
              />
            </FormRow>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header>Dane do faktury</Card.Header>
        <Card.Body>
          <Form className="d-grid gap-3">
            <FormRow controlId="invoiceName" label="Nazwa">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.invoiceName)}
                onChange={e => propertyChange({ invoiceName: e.target.value })}
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="invoiceAddress" label="Adres">
              <Form.Control
                type="text"
                value={textOrEmpty(examined?.invoiceAddress)}
                onChange={e =>
                  propertyChange({ invoiceAddress: e.target.value })
                }
                disabled={props.readOnly}
              />
            </FormRow>
            <FormRow controlId="nip" label="NIP PL/EU">
              <InputGroup>
                <Form.Control
                  type="text"
                  value={textOrEmpty(examined?.nip)}
                  onChange={e => propertyChange({ nip: e.target.value })}
                  disabled={props.readOnly}
                />
                <CompanyGusButton
                  readOnly={props.readOnly}
                  nip={examined?.nip}
                  isLoading={isGusLoading}
                  onClick={findByNip}
                  title="Pobierz dane do faktury z GUS"
                />
              </InputGroup>
            </FormRow>
          </Form>
          <ValidationAlert
            show={gusErrors.length > 0}
            errors={gusErrors}
            onHide={() => setGusErrors([])}
            className="mt-3"
          />
        </Card.Body>
      </Card>
    </>
  );
};
